import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { setBackendToken, setProfile } from '../../redux/slices/authSlice';
import { useExchangeTokenMutation, useLazyGetMeQuery } from '../../redux/api/authApi';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mapSupabaseErrorMessage } from './authErrors';
import PolicyDrawer from './components/PolicyDrawer';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import { Eye, EyeOff } from 'lucide-react';
import { useLazyGetDefaultPortfolioQuery, useLazyListPortfolioSitesQuery } from '../../redux/api/portfolioApi';
import { organizationApi } from '../../redux/api/organizationApi';
import { setSelectedPortfolioId, setPortfolios } from '../../redux/slices/portfolioSlice';
import { setSitesForPortfolio } from '../../redux/slices/siteSlice';

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [exchangeToken, { isLoading: isExchanging }] = useExchangeTokenMutation();
  const [triggerGetMe] = useLazyGetMeQuery();
  const [triggerGetDefaultPortfolio] = useLazyGetDefaultPortfolioQuery();
  const [triggerListPortfolioSites] = useLazyListPortfolioSitesQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [openPolicy, setOpenPolicy] = useState<{ type: 'terms' | 'privacy' | null }>({ type: null });
  const [showPassword, setShowPassword] = useState(false);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const emailLooksValid = /.+@.+\..+/.test(trimmedEmail);

    if (!trimmedEmail || !trimmedPassword) {
      toast.error('Please enter both email and password');
      return;
    }

    if (!emailLooksValid) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setIsLoading(true);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password: trimmedPassword,
      });
      if (signInError) throw signInError;

      const supabaseToken = data.session?.access_token;
      if (!supabaseToken) throw new Error('No Supabase session token');

      const invitationToken = localStorage.getItem('invitation_token');

      const resp = await exchangeToken({
        supabase_token: supabaseToken,
        device_info: {},
        ...(invitationToken ? { invitation_token: invitationToken } : {}),
      }).unwrap();

      if (invitationToken) {
        localStorage.removeItem('invitation_token');
      }

      dispatch(
        setBackendToken({
          accessToken: resp.access_token,
          tokenType: resp.token_type,
          expiresIn: resp.expires_in,
        })
      );

      const me = await triggerGetMe().unwrap();
      dispatch(
        setProfile({
          id: me.id ?? null,
          email: me.email ?? null,
          firstName: me.first_name ?? null,
          lastName: me.last_name ?? null,
          phone: me.phone ?? null,
          role: me.role ?? null,
          tenant: me.tenant ?? null, // Legacy field for backward compatibility
          active_tenant: (me as any).active_tenant ?? null,
          tenants: (me as any).tenants ?? null,
          activeSite: me.active_site ?? null,
          sessionId: me.session_id ?? null,
          permissions: me.permissions ?? null,
          isActive: typeof me.is_active === 'boolean' ? me.is_active : null,
          is_platform_admin: typeof (me as any).is_platform_admin === 'boolean' ? (me as any).is_platform_admin : null,
        })
      );
      
      // Extract tenant_id from profile
      const rawTenant = me.tenant as any;
      const tenantId = rawTenant?.id || rawTenant?.tenant_id || (me as any)?.tenant_id || null;
      
      // If user has a tenant, fetch organization first to establish context
      // This ensures the backend knows about the organization before portfolio requests
      if (tenantId) {
        try {
          // Fetch organization to establish tenant context in backend
          const orgResult = await dispatch(organizationApi.endpoints.getOrganization.initiate(tenantId));
          if (orgResult.data) {
          }
        } catch (orgErr) {
          console.error('Failed to fetch organization on login:', orgErr);
          // Continue even if organization fetch fails - might not be critical
        }
      }
      
      // Initialize portfolio and sites on login
      try {
        // Only fetch portfolio if user has a tenant
        if (!tenantId) {
          console.warn('No tenant_id found, skipping portfolio initialization');
        } else {
          // Get default portfolio
          const portfolio = await triggerGetDefaultPortfolio().unwrap();
        if (portfolio) {
          // Store portfolio in Redux
          dispatch(setPortfolios([portfolio]));
          dispatch(setSelectedPortfolioId(portfolio.id));
          
          // Fetch sites for the portfolio - await to ensure it completes before navigation
          try {
            const sitesResponse = await triggerListPortfolioSites({
              portfolio_id: portfolio.id,
            }).unwrap();
            const sites = Array.isArray(sitesResponse) 
              ? sitesResponse 
              : (sitesResponse as any)?.items || [];
            if (sites.length > 0) {
              dispatch(setSitesForPortfolio({ portfolioId: portfolio.id, sites }));
            }
          } catch (sitesErr) {
            console.error('Failed to fetch sites on login:', sitesErr);
            // Continue even if sites fetch fails
          }
        }
        }
      } catch (err) {
        console.error('Failed to initialize portfolio on login:', err);
        // Don't block login if portfolio fetch fails
      }
      
      navigate('/');
    } catch (err: any) {
      const msg = mapSupabaseErrorMessage(err?.message) || 'Login failed';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-start justify-center bg-[#0d1830] pt-20">
      <AnimatedBackground />
      <style>{`
        /* Override autofill styles for all states */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s !important;
          box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.08) !important;
          caret-color: white !important;
        }
        
        /* Ensure the fields maintain styling when typing after autofill */
        .login-input {
          background: rgba(255, 255, 255, 0.08) !important;
          backdrop-filter: blur(5px) !important;
          -webkit-backdrop-filter: blur(5px) !important;
          color: white !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div
          className="relative p-8 rounded-2xl w-full max-w-md"
          style={{
            background: 'rgba(0, 0, 0, 0.01)',
            backdropFilter: 'blur(3px) saturate(120%)',
            WebkitBackdropFilter: 'blur(3px) saturate(120%)',
            border: '0.5px solid rgba(255, 255, 255, 0.04)',
            boxShadow: '0 15px 32px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light text-white/90 tracking-wide">Login</h2>
          </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-input w-full px-3 py-2.5 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
          />
          <div className="space-y-1">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input w-full px-3 py-2.5 pr-10 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors focus:outline-none"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="inline-block py-1 text-white text-sm underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] focus:ring-offset-2 focus:ring-offset-[#0d1830] rounded">
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={isExchanging || isLoading}
            className="w-full py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(23, 196, 128, 0.9)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#0d1830'
            }}
          >
            {isExchanging || isLoading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div className="mt-3 text-white text-sm text-center">
          <span>
            Don't have an account?{' '}
            <Link to="/signup" className="inline-block py-1 underline text-white hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] focus:ring-offset-2 focus:ring-offset-[#0d1830] rounded">
              Sign up
            </Link>
          </span>
        </div>
        <div className="mt-4 text-white/70 text-xs text-center">
          <p>Need help? Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a></p>
        </div>
        </div>
      </div>
      <PolicyDrawer
        open={Boolean(openPolicy.type)}
        onClose={() => setOpenPolicy({ type: null })}
        title={openPolicy.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
      >
        {openPolicy.type === 'privacy' ? (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Privacy Policy</h3>
            <p>Last updated: July 8, 2025</p>
            <p>Engramiq ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.</p>
            <h4 className="font-semibold">1. Information We Collect</h4>
            <p className="font-semibold">Personal Information You Provide</p>
            <ul className="list-disc pl-6">
              <li>First and last name</li>
              <li>Email address</li>
              <li>Company name (optional)</li>
            </ul>
            <p className="font-semibold">Automatically Collected Information</p>
            <ul className="list-disc pl-6">
              <li>IP address and approximate location</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website information</li>
              <li>Device information (type, operating system)</li>
            </ul>
            <h4 className="font-semibold">2. How We Use Your Information</h4>
            <ul className="list-disc pl-6">
              <li>Process your inquiries</li>
              <li>Marketing communications (with your consent)</li>
              <li>Website analytics and improvements</li>
              <li>Legal compliance</li>
            </ul>
            <h4 className="font-semibold">3. Legal Basis for Processing (GDPR)</h4>
            <ul className="list-disc pl-6">
              <li>Consent</li>
              <li>Legitimate interests</li>
              <li>Legal obligation</li>
            </ul>
            <h4 className="font-semibold">4. Information Sharing and Disclosure</h4>
            <p className="font-semibold">Third-Party Service Providers</p>
            <ul className="list-disc pl-6">
              <li>HubSpot: CRM and email marketing (only with your consent)</li>
              <li>Hosting providers: to maintain and operate our website</li>
            </ul>
            <p>These providers are bound to protect your information and may only use it for authorized purposes.</p>
            <h4 className="font-semibold">5. Data Retention</h4>
            <ul className="list-disc pl-6">
              <li>Contact form data: up to 3 years or until deletion request</li>
              <li>Website analytics data: up to 26 months (anonymized)</li>
              <li>Marketing data: until you unsubscribe or request deletion</li>
            </ul>
            <h4 className="font-semibold">6. Your Privacy Rights</h4>
            <ul className="list-disc pl-6">
              <li>Access, rectification, erasure</li>
              <li>Restrict processing and object</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p>Contact: <a className="underline" href="mailto:privacy@engramiq.com">privacy@engramiq.com</a>. We respond within one month.</p>
            <h4 className="font-semibold">7. Cookies and Tracking Technologies</h4>
            <ul className="list-disc pl-6">
              <li>Essential cookies (always active)</li>
              <li>Analytics cookies (with consent)</li>
              <li>Marketing cookies (with consent)</li>
            </ul>
            <p>You can manage preferences via our cookie settings panel.</p>
            <h4 className="font-semibold">8. Data Security</h4>
            <p>We use appropriate technical and organizational measures including encryption, secure transmission, and regular assessments.</p>
            <h4 className="font-semibold">9. International Data Transfers</h4>
            <p>Your information may be transferred outside your country. We use standard contractual clauses and safeguards.</p>
            <h4 className="font-semibold">10. Children's Privacy</h4>
            <p>Not intended for children under 16; we do not knowingly collect data from them.</p>
            <h4 className="font-semibold">11. Changes to This Privacy Policy</h4>
            <p>We may update this policy; changes will be posted with an updated date.</p>
            <h4 className="font-semibold">12. Contact Us</h4>
            <p>Email: <a className="underline" href="mailto:privacy@engramiq.com">privacy@engramiq.com</a></p>
            <h4 className="font-semibold">13. Supervisory Authority</h4>
            <p>EU users may lodge complaints with their local data protection authority.</p>
          </div>
        ) : openPolicy.type === 'terms' ? (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Terms of Service</h3>
            <p>(Last updated: August 19, 2025)</p>
            <h4 className="font-semibold">1. Agreement to Terms</h4>
            <p>By accessing or using the Engramiq platform, including our mobile and desktop applications (the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Services on behalf of a company, you represent that you have authority to bind that company.</p>
            <h4 className="font-semibold">2. Scope & Access</h4>
            <ul className="list-disc pl-6">
              <li>Business use only.</li>
              <li>Access per your subscription or licence agreement.</li>
              <li>You must keep credentials secure and comply with these Terms.</li>
            </ul>
            <h4 className="font-semibold">3. Your Responsibilities</h4>
            <ul className="list-disc pl-6">
              <li>Comply with applicable laws.</li>
              <li>Do not copy, reverse engineer, or bypass security.</li>
              <li>Do not upload unlawful or infringing content.</li>
              <li>Ensure accuracy and rights for data you provide.</li>
            </ul>
            <h4 className="font-semibold">4. Our Responsibilities</h4>
            <ul className="list-disc pl-6">
              <li>Provide Services professionally.</li>
              <li>Maintain reasonable security measures.</li>
              <li>Comply with data protection laws (UK, EU, US).</li>
            </ul>
            <h4 className="font-semibold">5. Data Protection & Privacy</h4>
            <p>Use of personal data is governed by our Privacy Policy. You are the controller for data you input; we act as processor per our DPA where applicable.</p>
            <h4 className="font-semibold">6. Intellectual Property</h4>
            <ul className="list-disc pl-6">
              <li>All IP in the Services belongs to or is licensed to us.</li>
              <li>You retain rights to your data and content.</li>
              <li>We may use aggregated/anonymised data for analytics and improvements.</li>
            </ul>
            <h4 className="font-semibold">7. Confidentiality</h4>
            <p>Both parties will protect each other's confidential information.</p>
            <h4 className="font-semibold">8. Warranties & Disclaimers</h4>
            <p>Services provided "as is" except as expressly stated; we disclaim implied warranties to the fullest extent permitted by law.</p>
            <h4 className="font-semibold">9. Limitation of Liability</h4>
            <ul className="list-disc pl-6">
              <li>No liability for indirect or consequential losses.</li>
              <li>Total liability limited to fees paid in the relevant 12‑month period.</li>
            </ul>
            <h4 className="font-semibold">10. Suspension & Termination</h4>
            <p>We may suspend/terminate for breach or nonpayment; we will allow reasonable data export.</p>
            <h4 className="font-semibold">11. Governing Law</h4>
            <ul className="list-disc pl-6">
              <li>England and Wales (UK/EU users)</li>
              <li>Delaware, USA (US/other users)</li>
            </ul>
            <p>Disputes resolved in the applicable courts above.</p>
            <h4 className="font-semibold">12. Changes to Terms</h4>
            <p>We may update these Terms; material changes will be noticed before taking effect.</p>
          </div>
        ) : null}
      </PolicyDrawer>
      {/* <SupportChat /> */}
    </div>
  );
};

export default LoginPage;